import { useState } from "react";
import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import type { ChainLevel, NsChain } from "@/api/netsuiteMap";
import { MONO, TONES } from "./shared";

// Each cycle as the chain of NetSuite records it produces, with who or what
// creates each one and how much a person still has to do.
const LEVELS: Record<ChainLevel, { label: string; tone: { fg: string; bg: string } }> = {
  automated: { label: "Automated", tone: TONES.auto },
  approved: { label: "Person approves", tone: TONES.review },
  by_hand: { label: "By hand", tone: TONES.manual },
  monitored: { label: "Monitored", tone: TONES.watch },
};

export function LevelPill({ level, label }: { level: ChainLevel; label?: string }) {
  const { tone } = LEVELS[level];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.6, fontFamily: MONO, fontSize: 10.5, letterSpacing: ".04em",
        textTransform: "uppercase", px: 0.9, py: "2px", borderRadius: 999, color: tone.fg, bgcolor: tone.bg,
        "&::before": { content: "\"\"", width: 7, height: 7, borderRadius: "50%", bgcolor: "currentColor" },
      }}
    >
      {label ?? LEVELS[level].label}
    </Box>
  );
}

export function NewTag({ children = "New 2026" }: { children?: string }) {
  return (
    <Box component="span" sx={{ fontFamily: MONO, fontSize: 10.5, color: TONES.accent.fg, bgcolor: TONES.accent.bg, px: 0.75, py: "1px", borderRadius: "3px", whiteSpace: "nowrap" }}>
      {children}
    </Box>
  );
}

export function ChainLegend() {
  return (
    <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
      <LevelPill level="automated" />
      <LevelPill level="approved" label="Prepared, person approves" />
      <LevelPill level="by_hand" label="Keyed by hand" />
      <LevelPill level="monitored" />
      <NewTag />
    </Stack>
  );
}

export function DocumentChains({ chains }: { chains: NsChain[] }) {
  const [cur, setCur] = useState(0);
  const chain = chains[cur];
  return (
    <Stack spacing={2}>
      <Tabs value={cur} onChange={(_, v: number) => setCur(v)} variant="scrollable" allowScrollButtonsMobile sx={{ borderBottom: 1, borderColor: "divider" }}>
        {chains.map((c) => <Tab key={c.id} label={c.title} />)}
      </Tabs>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "72ch" }}>{chain.intro}</Typography>
      <Box
        sx={{
          display: "grid",
          gridAutoFlow: { xs: "row", md: "column" },
          gridAutoColumns: { md: "minmax(200px, 1fr)" },
          gap: { xs: 2, md: 0 },
          overflowX: { md: "auto" },
          pb: 0.75,
        }}
      >
        {chain.steps.map((s, i) => (
          <Box
            key={s.record}
            sx={{
              position: "relative", pr: { md: 2.25 }, mr: { md: 2.25 }, display: "grid", alignContent: "start", gap: 1.25, minWidth: 0,
              ...(i < chain.steps.length - 1 && {
                "&::after": {
                  content: "\"\"", position: "absolute", width: 10, height: 10, borderTop: 2, borderRight: 2, borderColor: "text.secondary",
                  top: { md: 22 }, right: { md: -14 }, bottom: { xs: -12, md: "auto" }, left: { xs: 14, md: "auto" },
                  transform: { xs: "rotate(135deg)", md: "rotate(45deg)" },
                },
              }),
            }}
          >
            <Box sx={{ borderTop: 3, borderColor: "text.primary", pt: 1.25 }}>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>{s.record}</Typography>
              <Typography sx={{ fontFamily: MONO, fontSize: 11.5, color: "text.secondary" }}>{s.netsuite_type}</Typography>
            </Box>
            {s.by.map((b) => (
              <Box key={b.who + b.note} sx={{ fontSize: 13.5, lineHeight: 1.45, display: "grid", gap: 0.5, p: 1.25, borderRadius: 1.5, bgcolor: "action.hover" }}>
                <Stack direction="row" gap={0.75} flexWrap="wrap" alignItems="center">
                  <LevelPill level={b.level} />
                  {b.new_2026 && <NewTag />}
                </Stack>
                <span><b>{b.who}</b>: {b.note}</span>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Stack>
  );
}
